import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AuthForm.css';
import { signUp, login, forgotPassword, resetPassword } from '../services/User-Service'

const AuthForm = ({ onLoginSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [resetData, setResetData] = useState({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    mobile: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validateForm = (isLogin, formData) => {
    const newErrors = {};

    if (!isLogin) {
      const firstName = formData.firstName.trim();
      const lastName = formData.lastName.trim();
      const middleName = formData.middleName.trim();

      if (!firstName) {
        newErrors.firstName = 'First name is required';
      } else if (!/^[A-Za-z\s]+$/.test(firstName)) {
        newErrors.firstName = 'First name should contain only letters';
      }

      if (middleName && !/^[A-Za-z\s]*$/.test(middleName)) {
        newErrors.middleName = 'Middle name should contain only letters';
      }

      if (!lastName) {
        newErrors.lastName = 'Last name is required';
      } else if (!/^[A-Za-z\s]+$/.test(lastName)) {
        newErrors.lastName = 'Last name should contain only letters';
      }

      const mobile = formData.mobile.trim();
      if (!mobile) {
        newErrors.mobile = 'Mobile number is required';
      } else if (!/^\d{10}$/.test(mobile)) {
        newErrors.mobile = 'Mobile number must be exactly 10 digits';
      }
    }

    const email = formData.email.trim();
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }

    const password = formData.password.trim();
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (!isLogin && password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!isLogin && !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
    }

    return newErrors;
  };

  const handleToggle = () => {
    setIsLogin(!isLogin);
    setErrors({});
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handleForgotPasswordSubmit = (e) => {
    e.preventDefault();
    resetStep === 1 ? handleSendResetCode() : handleResetPassword();
  };

  const validateEmail = (email) => {
    if (!email.trim()) return 'Email is required';
    if (!/\S+@\S+\.\S+/.test(email)) return 'Invalid email address';
    return '';
  };

  const handleSendResetCode = () => {
    const emailError = validateEmail(resetData.email);
    if (emailError) {
      setErrors({ email: emailError });
      return;
    }
    setLoading(true);
    forgotPassword(resetData.email)
      .then(() => setResetStep(2))
      .catch((error) => setErrors({ general: error.message }))
      .finally(() => setLoading(false));
  };

  const handleResetPassword = () => {
    const newErrors = {};
    if (!resetData.code.trim()) newErrors.code = 'Code is required';

    const newPassword = resetData.newPassword.trim();
    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(newPassword)) {
      newErrors.newPassword = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
    }

    if (resetData.newPassword !== resetData.confirmPassword)
      newErrors.confirmPassword = 'Passwords do not match';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    resetPassword(resetData)
      .then(() => {
        alert('Password reset successful!');
        setIsForgotPassword(false);
        setResetStep(1);
        setResetData({ email: '', code: '', newPassword: '', confirmPassword: '' });
      })
      .catch((error) => setErrors({ general: error.message }))
      .finally(() => setLoading(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formErrors = validateForm(isLogin, formData);
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setLoading(true);
    const processedData = {
      ...formData,
      firstName: formData.firstName.trim(),
      middleName: formData.middleName.trim(),
      lastName: formData.lastName.trim(),
      mobile: formData.mobile.trim(),
      email: formData.email.trim()
    };

    if (isLogin) {
      login(processedData)
        .then((userData) => {
          onLoginSuccess(userData.role);
          navigate(userData.role === 'ROLE_ADMIN' ? '/admin/dashboard' : '/booking');
        })
        .catch((error) => {
          console.log("Login error:", error);

          // Handle various error responses
          if (error.response) {
            const status = error.response.status;
            const errorData = error.response.data;

            // Case 1: User not registered (404)
            if (status === 404 || (errorData && errorData.message && errorData.message.includes("not found"))) {
              setErrors({ email: "Email not registered. Please register first." });
            }
            // Case 2: Invalid password (401 with password message)
            else if (status === 401 || (errorData && errorData.message && errorData.message.includes("password"))) {
              setErrors({ password: "Entered password is incorrect." });
            }
            // Case 3: Invalid email format or other email issues
            else if (errorData && errorData.message && errorData.message.includes("email")) {
              setErrors({ email: "Entered email is incorrect." });
            }
            // Default fallback for other errors
            else {
              setErrors({ general: "Login failed please enter valid username and password." });
            }
          } else {
            // Network errors or other issues
            setErrors({ general: "Cannot connect to server. Please try again later." });
          }
        })
        .finally(() => setLoading(false));
    } else {
      signUp(processedData)
        .then(() => {
          alert('Registration successful!');
          setIsLogin(true);
          setFormData({ firstName: '', middleName: '', lastName: '', mobile: '', email: '', password: '' });
        })
        .catch(() => setErrors({ general: <b>{'This email is already registered, please login.'}</b> }))
        .finally(() => setLoading(false));
    }
  };

  return (
    <div className="auth-wrapper">
      <div className={`auth-container ${isLogin ? '' : 'active'}`}>
        <div className="welcome-section">
          <h2>{isLogin ? 'Hello, Welcome!' : 'Welcome'}</h2>
          <p className='suggestions'>{isLogin ? "Don't have an account?" : "Already have an account?"}</p>
          <button onClick={handleToggle} className="toggle-button">
            {isLogin ? 'Register' : 'Login'}
          </button>
        </div>

        <div className="form-section">
          <h2>{isForgotPassword ? 'Reset Password' : isLogin ? 'Login' : 'Register'}</h2>
          {errors.general && <div className="error-message error-box">{errors.general}</div>}

          {isForgotPassword ? (
            <form onSubmit={handleForgotPasswordSubmit} className="reset-password-form">
              {resetStep === 1 ? (
                <>
                  <div className="form-group">
                    <label htmlFor="forgot-email">Email</label>
                    <div className="input-wrapper">
                      <input
                        type="email"
                        id="forgot-email"
                        name="email"
                        placeholder="Enter your email"
                        value={resetData.email}
                        onChange={(e) => setResetData({ ...resetData, email: e.target.value })}
                      />
                      {errors.email && <div className="error-message">{errors.email}</div>}
                    </div>
                  </div>
                  <button type="submit" className="submit-button" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Reset Code'}
                  </button>
                  <button type="button" className="toggle-button" onClick={() => setIsForgotPassword(false)}>
                    Back to Login
                  </button>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="code">Reset Code</label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        id="code"
                        name="code"
                        placeholder="Enter reset code"
                        value={resetData.code}
                        onChange={(e) => setResetData({ ...resetData, code: e.target.value })}
                      />
                      {errors.code && <div className="error-message">{errors.code}</div>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>New Password</label>
                    <div className="input-wrapper">
                      <input
                        type="password"
                        placeholder="New Password"
                        value={resetData.newPassword}
                        onChange={(e) => setResetData({ ...resetData, newPassword: e.target.value })}
                      />
                      {errors.newPassword && <div className="error-message">{errors.newPassword}</div>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Confirm Password</label>
                    <div className="input-wrapper">
                      <input
                        type="password"
                        placeholder="Confirm Password"
                        value={resetData.confirmPassword}
                        onChange={(e) => setResetData({ ...resetData, confirmPassword: e.target.value })}
                      />
                      {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
                    </div>
                  </div>

                  <button type="submit" className="submit-button" disabled={loading}>
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>
                  <button type="button" className="toggle-button" onClick={() => setResetStep(1)}>
                    Back
                  </button>
                </>
              )}
            </form>
          ) : (
            <>
              <form onSubmit={handleSubmit}>
                {isLogin ? (
                  <>
                    <div className="form-group">
                      <label htmlFor="login-email">Email</label>
                      <div className="input-wrapper">
                        <input
                          type="email"
                          id="login-email"
                          name="email"
                          placeholder="Enter email"
                          value={formData.email}
                          onChange={handleChange}
                        />
                        {errors.email && <div className="error-message">{errors.email}</div>}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="password">Password</label>
                      <div className="input-wrapper">
                        <input
                          type="password"
                          id="password"
                          name="password"
                          placeholder="Enter password"
                          value={formData.password}
                          onChange={handleChange}
                        />
                        {errors.password && <div className="error-message">{errors.password}</div>}
                      </div>
                    </div>

                    <div className="forgot-password-link">
                      <button type="button" onClick={() => setIsForgotPassword(true)}>
                        Forgot Password?
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Full Name</label>
                      <div className="name-inputs-container">
                        <div className="input-wrapper">
                          <input
                            type="text"
                            name="firstName"
                            placeholder="First name"
                            value={formData.firstName}
                            onChange={handleChange}
                            onKeyPress={(e) => {
                              if (!/^[A-Za-z\s]$/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          />
                          {errors.firstName && <div className="error-message">{errors.firstName}</div>}
                        </div>
                        <div className="input-wrapper">
                          <input
                            type="text"
                            name="middleName"
                            placeholder="Middle name"
                            value={formData.middleName}
                            onChange={handleChange}
                            onKeyPress={(e) => {
                              if (!/^[A-Za-z\s]$/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          />
                          {errors.middleName && <div className="error-message">{errors.middleName}</div>}
                        </div>
                        <div className="input-wrapper">
                          <input
                            type="text"
                            name="lastName"
                            placeholder="Last name"
                            value={formData.lastName}
                            onChange={handleChange}
                            onKeyPress={(e) => {
                              if (!/^[A-Za-z\s]$/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          />
                          {errors.lastName && <div className="error-message">{errors.lastName}</div>}
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="mobile">Mobile Number</label>
                      <div className="input-wrapper">
                        <input
                          type="tel"
                          name="mobile"
                          placeholder="Enter 10-digit mobile number"
                          maxLength="10"
                          value={formData.mobile}
                          onChange={handleChange}
                          onKeyPress={(e) => {
                            if (!/^[0-9]$/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                        />
                        {errors.mobile && <div className="error-message">{errors.mobile}</div>}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email</label>
                      <div className="input-wrapper">
                        <input type="email" name="email" placeholder="Enter email" value={formData.email} onChange={handleChange} />
                        {errors.email && <div className="error-message">{errors.email}</div>}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="password">Password</label>
                      <div className="input-wrapper">
                        <input
                          type="password"
                          name="password"
                          placeholder="Enter password (min 8 characters)"
                          value={formData.password}
                          onChange={handleChange}
                        />
                        {errors.password && <div className="error-message">{errors.password}</div>}
                        {!errors.password && formData.password && formData.password.length > 0 && formData.password.length < 8 &&
                          <div className="password-hint">Password must be at least 8 characters</div>}
                      </div>

                    </div>
                  </>
                )}
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? 'Processing...' : isLogin ? 'Login' : 'Register'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthForm;